import { useEffect, useMemo, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import Collaboration from '@tiptap/extension-collaboration'
import { HocuspocusProvider } from '@hocuspocus/provider'
import * as Y from 'yjs'
import { baseEditorExtensions } from './editorExtensions'
import { uploadImage } from './api'
import { FindBar } from './FindBar'
import { VersionHistory } from './VersionHistory'
import { Devices } from './Devices'
import { Toolbar } from './Toolbar'

type SyncState = 'connecting' | 'synced' | 'saving' | 'offline'
type Panel = 'none' | 'menu' | 'find' | 'versions' | 'devices'

const weekday = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']

function todayLabel(): string {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day} ${weekday[date.getDay()]}`
}

function websocketUrl(): string {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${location.host}/collaboration`
}

export function FocusEditor({ onUnauthorized }: { onUnauthorized: () => void }) {
  const ydoc = useMemo(() => new Y.Doc(), [])
  const imageInput = useRef<HTMLInputElement>(null)
  const restoredPosition = useRef(false)
  const scrollTimer = useRef<number | null>(null)
  const [syncState, setSyncState] = useState<SyncState>('connecting')
  const [panel, setPanel] = useState<Panel>('none')
  const [uploading, setUploading] = useState(false)

  const editor = useEditor({
    extensions: [
      ...baseEditorExtensions,
      Collaboration.configure({ document: ydoc }),
    ],
    editable: false,
    editorProps: {
      attributes: {
        class: 'focusnote-document',
        spellcheck: 'true',
        'aria-label': 'FocusNote 主笔记',
        // 划词改写类浏览器扩展（Grammarly 等）会直接改动 contenteditable 的 DOM，
        // 而 ProseMirror 会把 DOM 变更当作真实编辑提交进文档 —— 那是"字被吞掉"的经典成因，
        // 而且是间歇性的、从服务端完全看不出原因。这里把它们挡掉。
        'data-gramm': 'false',
        'data-gramm_editor': 'false',
        'data-enable-grammarly': 'false',
      },
      transformPastedHTML: (html) => html.replace(/<img\b[^>]*>/gi, ''),
      handlePaste: (view, event) => {
        const file = Array.from(event.clipboardData?.files ?? [])
          .find((item) => item.type.startsWith('image/'))
        if (!file) return false
        setUploading(true)
        void uploadImage(file)
          .then(({ url }) => {
            const imageNode = view.state.schema.nodes.image.create({ src: url })
            view.dispatch(view.state.tr.replaceSelectionWith(imageNode).scrollIntoView())
          })
          .finally(() => setUploading(false))
        return true
      },
      handleKeyDown: (_view, event) => {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
          event.preventDefault()
          setPanel('find')
          return true
        }
        return false
      },
    },
    onSelectionUpdate: ({ editor: currentEditor }) => {
      localStorage.setItem('focusnote-cursor', String(currentEditor.state.selection.from))
    },
  })

  useEffect(() => {
    if (!editor) return
    const debug = {
      events: [] as string[],
      provider: null as HocuspocusProvider | null,
      editor,
    }
    if (import.meta.env.DEV) {
      ;(window as Window & { __focusDebug?: typeof debug }).__focusDebug = debug
    }
    const provider = new HocuspocusProvider({
      url: websocketUrl(),
      name: 'main',
      document: ydoc,
      // Hocuspocus starts its authentication handshake only when a token is
      // present. The real credential remains in the HttpOnly cookie.
      token: 'device-cookie',
      onStatus: ({ status }) => {
        debug.events.push(`status:${status}`)
        if (status === 'connected') setSyncState(provider.synced ? 'synced' : 'connecting')
        if (status === 'connecting') setSyncState('connecting')
        if (status === 'disconnected') setSyncState('offline')
      },
      onSynced: ({ state }) => {
        debug.events.push(`synced:${state}`)
        setSyncState(state ? 'synced' : 'connecting')
      },
      onUnsyncedChanges: ({ number }) => {
        debug.events.push(`unsynced:${number}`)
        if (provider.synced) setSyncState(number > 0 ? 'saving' : 'synced')
      },
      onAuthenticationFailed: () => {
        debug.events.push('authentication-failed')
        onUnauthorized()
      },
    })
    debug.provider = provider

    return () => {
      provider.destroy()
    }
  }, [editor, onUnauthorized, ydoc])

  useEffect(() => {
    if (!editor) return
    const editable = syncState === 'synced' || syncState === 'saving'
    editor.setEditable(editable)

    if (editable && !restoredPosition.current) {
      restoredPosition.current = true
      const storedPosition = Number(localStorage.getItem('focusnote-cursor'))
      const position = Number.isFinite(storedPosition)
        ? Math.min(Math.max(storedPosition, 1), Math.max(editor.state.doc.content.size - 1, 1))
        : 1
      editor.commands.setTextSelection(position)
      const storedScroll = Number(localStorage.getItem('focusnote-scroll'))
      requestAnimationFrame(() => {
        if (Number.isFinite(storedScroll)) window.scrollTo({ top: storedScroll })
        const desktop = matchMedia('(pointer:fine)').matches
        if (desktop) editor.commands.focus(position)
      })
    }
  }, [editor, syncState])

  useEffect(() => {
    function rememberScroll() {
      if (scrollTimer.current) window.clearTimeout(scrollTimer.current)
      scrollTimer.current = window.setTimeout(() => {
        localStorage.setItem('focusnote-scroll', String(window.scrollY))
      }, 150)
    }
    window.addEventListener('scroll', rememberScroll, { passive: true })
    return () => window.removeEventListener('scroll', rememberScroll)
  }, [])

  if (!editor) return null

  function goToToday() {
    if (!editor) return
    const label = todayLabel()
    let foundPosition: number | null = null
    editor.state.doc.descendants((node, position) => {
      if (node.type.name === 'heading' && node.textContent.trim() === label) {
        foundPosition = position + 1
        return false
      }
    })

    if (foundPosition !== null) {
      editor.chain().focus().setTextSelection(foundPosition).scrollIntoView().run()
      return
    }

    editor.chain()
      .focus('end')
      .insertContent([
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: label }],
        },
        {
          type: 'taskList',
          content: [{
            type: 'taskItem',
            attrs: { checked: false, taskId: crypto.randomUUID(), dueAt: null },
            content: [{ type: 'paragraph' }],
          }],
        },
      ])
      .run()
  }

  async function insertImage(file: File | undefined) {
    if (!file || !editor) return
    setUploading(true)
    try {
      const { url } = await uploadImage(file)
      editor.chain().focus().setImage({ src: url }).run()
    } finally {
      setUploading(false)
      if (imageInput.current) imageInput.current.value = ''
    }
  }

  const statusText = {
    connecting: '正在连接',
    synced: '已同步',
    saving: '正在保存',
    offline: '连接中断，已暂停编辑',
  }[syncState]
  const canEdit = syncState === 'synced' || syncState === 'saving'

  return (
    <>
      <header className="topbar">
        <button className="today-button" onClick={goToToday} disabled={!canEdit}>今天</button>
        <div className={`sync-status ${syncState}`} role="status">
          <span />
          {statusText}
        </div>
        <button
          className="icon-button menu-trigger"
          aria-label="打开菜单"
          onClick={() => setPanel(panel === 'menu' ? 'none' : 'menu')}
        >
          •••
        </button>
        {panel === 'menu' && (
          <nav className="popover main-menu" aria-label="笔记菜单">
            <button onClick={() => setPanel('find')}>查找</button>
            <button onClick={() => setPanel('versions')}>版本历史</button>
            <button onClick={() => setPanel('devices')}>受信设备</button>
            <a href="/api/export" download>导出备份</a>
          </nav>
        )}
      </header>

      <Toolbar
        editor={editor}
        canEdit={canEdit}
        onPickImage={() => imageInput.current?.click()}
      />

      {panel === 'find' && <FindBar editor={editor} onClose={() => setPanel('none')} />}

      {syncState === 'offline' && (
        <div className="offline-banner">网络连接已中断。为避免冲突，暂时停止输入；重连后会自动继续。</div>
      )}

      <main className="editor-shell">
        <EditorContent editor={editor} />
      </main>

      <input
        ref={imageInput}
        className="visually-hidden"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(event) => void insertImage(event.target.files?.[0])}
      />

      {uploading && <div className="upload-toast" role="status">正在上传图片…</div>}

      {panel === 'versions' && <VersionHistory onClose={() => setPanel('none')} />}
      {panel === 'devices' && (
        <Devices
          onClose={() => setPanel('none')}
          onCurrentRevoked={onUnauthorized}
        />
      )}
    </>
  )
}
